import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PaymentLink, PaymentLinkMethod } from '@masonic-tools/shared-types';
import { formatDateTime, linkDisplayState, relativeDays, type LinkDisplayState } from '@/lib/unit-payment-links';

interface Props {
  link: Pick<PaymentLink, 'status' | 'expiresAt' | 'paymentMethod'>;
  nowMs: number;
  availableMethods?: PaymentLinkMethod[];
  className?: string;
}

const LABELS: Record<LinkDisplayState, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  disabled: 'Disabled',
  archived: 'Archived',
  method_unavailable: 'Payments unavailable',
};

const VARIANTS: Record<LinkDisplayState, 'success' | 'warning' | 'outline' | 'secondary' | 'destructive'> = {
  active: 'success',
  expiring: 'warning',
  expired: 'outline',
  disabled: 'secondary',
  archived: 'secondary',
  method_unavailable: 'destructive',
};

export function PaymentLinkStatusBadge({ link, nowMs, availableMethods, className }: Props) {
  const state = linkDisplayState(link, nowMs, availableMethods);
  const label =
    state === 'expiring' && link.expiresAt ? `Expires ${relativeDays(link.expiresAt.getTime(), nowMs)}` : LABELS[state];

  const badge = (
    <Badge variant={VARIANTS[state]} className={className}>
      {label}
    </Badge>
  );

  if (state === 'method_unavailable') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{badge}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            The payment provider for this link is no longer connected. Payers will see a "temporarily unavailable" message
            until you reconnect it in Unit Settings or choose another payment method.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if ((state === 'expiring' || state === 'expired') && link.expiresAt) {
    return (
      <span title={formatDateTime(link.expiresAt)} className="inline-flex">
        {badge}
      </span>
    );
  }
  return badge;
}

export function ReferenceChip({ reference, className = '' }: { reference: string; className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-xs tracking-wide text-muted-foreground ${className}`}
    >
      {reference}
    </span>
  );
}
